import { useRef } from "react";
import { BadgeCheck, MessageSquareQuote, ChevronLeft, ChevronRight } from "lucide-react";

const PROOF_IMAGE =
  "https://customer-assets-m6fa6gv7.emergentagent.net/job_hemat-indihome/artifacts/171f4e40ecee185d_IMG-20261014-WA0018%281%29.jpg";

const TESTIMONIALS = [
  {
    url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_hemat-indihome/artifacts/a77713e236bd6302_Screenshot_20261020_073525_WhatsAppBusiness.webp",
    quote: "Makasih ya bang.. rekomended",
    alt: "Testimoni pelanggan: migrasi selesai dan paket retensi aktif",
  },
  {
    url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_hemat-indihome/artifacts/e6ff7de7f7f59be8_Screenshot_20261020_074532_WhatsAppBusiness.webp",
    quote: "Makasih ya kak sudah done — nanti saya rekomendasi ke temen-temen juga",
    alt: "Testimoni pelanggan: pembayaran fee setelah migrasi selesai",
  },
  {
    url: "https://customer-assets-m6fa6gv7.emergentagent.net/job_hemat-indihome/artifacts/e54d7a5c0501e1e0_Screenshot_20261020_074514_WhatsAppBusiness.webp",
    quote: "Makasih banyak ya bro, sukses selalu, moga banyak yang pake jasanya",
    alt: "Testimoni pelanggan: bukti pembayaran jasa migrasi",
  },
];

export const ProofSection = () => {
  const galleryRef = useRef(null);

  const scroll = (dir) => {
    galleryRef.current?.scrollBy({
      left: dir * (galleryRef.current.clientWidth * 0.8),
      behavior: "smooth",
    });
  };

  return (
    <section
      id="bukti"
      data-testid="proof-section"
      className="py-20 md:py-32 bg-[#F8F9FA]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="max-w-2xl">
          <p className="text-xs md:text-sm font-semibold uppercase tracking-widest text-[#E60000]">
            Bukti Nyata
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-gray-900 mt-4">
            Bukan Janji, Ini Hasilnya
          </h2>
          <p className="text-base text-gray-600 mt-4 leading-relaxed">
            Paket pelanggan benar-benar berubah ke paket retensi dan bisa dicek
            sendiri langsung di aplikasi MyTelkomsel.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 mt-14">
          <div
            data-testid="proof-migration-card"
            className="lg:col-span-2 bg-white rounded-3xl border border-black/10 overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300"
          >
            <div className="bg-gray-950 px-6 py-4 flex items-center gap-3">
              <BadgeCheck className="w-5 h-5 text-green-400" />
              <p className="font-heading font-bold text-white text-sm md:text-base">
                Bukti Paket Berhasil Berubah
              </p>
            </div>
            <div className="p-6 flex justify-center bg-white">
              <img
                src={PROOF_IMAGE}
                alt="Tampilan aplikasi menunjukkan paket New Retensi Internet 300 Mbps aktif setelah migrasi"
                data-testid="proof-migration-image"
                className="rounded-2xl w-full max-w-xs border border-black/5 shadow-md"
                loading="lazy"
              />
            </div>
            <p className="text-sm text-gray-600 leading-relaxed px-6 pb-6">
              Contoh paket pelanggan yang sudah berubah menjadi{" "}
              <span className="font-semibold text-gray-900">
                New Retensi Internet 300 Mbps
              </span>{" "}
              setelah proses migrasi selesai — bisa kamu cek sendiri di aplikasi
              MyTelkomsel.
            </p>
          </div>

          <div
            data-testid="proof-testimonial-gallery"
            className="lg:col-span-3 bg-white rounded-3xl border border-black/10 overflow-hidden"
          >
            <div className="bg-gray-950 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MessageSquareQuote className="w-5 h-5 text-green-400" />
                <p className="font-heading font-bold text-white text-sm md:text-base">
                  Testimoni Pelanggan
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  data-testid="testimonial-prev-button"
                  onClick={() => scroll(-1)}
                  aria-label="Testimoni sebelumnya"
                  className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center transition-colors"
                >
                  <ChevronLeft className="w-4 h-4 text-white" />
                </button>
                <button
                  data-testid="testimonial-next-button"
                  onClick={() => scroll(1)}
                  aria-label="Testimoni berikutnya"
                  className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/25 flex items-center justify-center transition-colors"
                >
                  <ChevronRight className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            <div
              ref={galleryRef}
              data-testid="testimonial-scroll-container"
              className="flex gap-5 overflow-x-auto snap-x snap-mandatory p-6 [scrollbar-width:thin]"
            >
              {TESTIMONIALS.map((t, i) => (
                <figure
                  key={t.url}
                  data-testid={`testimonial-slide-${i + 1}`}
                  className="snap-center shrink-0 w-64 md:w-72"
                >
                  <img
                    src={t.url}
                    alt={t.alt}
                    className="rounded-2xl w-full aspect-[9/16] object-cover object-top border border-black/5 shadow-md"
                    loading="lazy"
                  />
                  <figcaption className="mt-3 text-sm text-gray-700 italic leading-relaxed">
                    “{t.quote}”
                  </figcaption>
                </figure>
              ))}
            </div>
            <p className="text-xs text-gray-400 px-6 pb-5">
              Geser untuk melihat testimoni lainnya.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
